import { ReactElement, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faXmark,
  faShieldHalved,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import {
  BillingPeriod,
  CreateKeyInput,
  CreateKeyResponse,
  Currency,
  KnownScopeDto,
  PlanType,
} from "../../../../data/clients/ExternalApiClient";
import { ConfirmDialog, isAdminScope } from "../shared/ConfirmDialog";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { Organization } from "../../../../core/models/entities/Organization";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (input: CreateKeyInput) => Promise<CreateKeyResponse>;
  knownScopes: KnownScopeDto[];
}

const CURRENCIES: Currency[] = ["DKK", "EUR", "USD", "GBP"];
const PLAN_TYPES: { value: PlanType; label: string; sub: string }[] = [
  { value: "FreeTierCap", label: "Gratis", sub: "Hård grænse, ingen genopfyldning" },
  { value: "FlatPerCall", label: "Pris per kald", sub: "Betal for hvert kald" },
  {
    value: "MonthlyWithOverage",
    label: "Abonnement + overforbrug",
    sub: "Grundgebyr med inkluderede kald",
  },
];

export function CreateKeyDialog({
  open,
  onClose,
  onCreate,
  knownScopes,
}: Props): ReactElement {
  const { organizationRepository } = useRepositoriesContext();
  const { whiteLabel } = useWhiteLabelContext();
  const [step, setStep] = useState<1 | 2 | 3 | "done">(1);
  const [selectedVendor, setSelectedVendor] = useState<Organization | null>(
    null,
  );
  const [vendorQuery, setVendorQuery] = useState<string>("");
  const [vendorOptions, setVendorOptions] = useState<Organization[]>([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [name, setName] = useState<string>("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [planType, setPlanType] = useState<PlanType>("FreeTierCap");
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("Monthly");
  const [baseFee, setBaseFee] = useState<string>("");
  const [includedCalls, setIncludedCalls] = useState<string>("1000");
  const [overageRate, setOverageRate] = useState<string>("");
  const [currency, setCurrency] = useState<Currency>("DKK");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [pendingAdminScope, setPendingAdminScope] = useState<string | null>(
    null,
  );

  function reset() {
    setStep(1);
    setSelectedVendor(null);
    setVendorQuery("");
    setVendorOptions([]);
    setName("");
    setScopes([]);
    setPlanType("FreeTierCap");
    setBillingPeriod("Monthly");
    setBaseFee("");
    setIncludedCalls("1000");
    setOverageRate("");
    setCurrency("DKK");
    setError(null);
    setCreated(null);
    setCopied(false);
  }

  // Debounced vendor search
  useEffect(() => {
    if (!open) return;
    const q = vendorQuery.trim();
    if (q.length < 2) {
      setVendorOptions([]);
      return;
    }
    setVendorLoading(true);
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const results = await organizationRepository.searchAdminVendors(q);
        if (!cancelled) setVendorOptions(results);
      } catch {
        if (!cancelled) setVendorOptions([]);
      } finally {
        if (!cancelled) setVendorLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [vendorQuery, open, organizationRepository]);

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    try {
      if (!selectedVendor) {
        setError("Vælg først en forhandler.");
        setBusy(false);
        return;
      }
      const input: CreateKeyInput = {
        organizationId: selectedVendor.id,
        name,
        scopes,
        plan: {
          planType,
          currency,
          billingPeriod:
            planType === "MonthlyWithOverage" ? billingPeriod : undefined,
          baseFee:
            planType === "MonthlyWithOverage" ? Number(baseFee) : undefined,
          includedCalls:
            planType === "FreeTierCap" || planType === "MonthlyWithOverage"
              ? Number(includedCalls)
              : undefined,
          overageRate:
            planType === "FlatPerCall" || planType === "MonthlyWithOverage"
              ? Number(overageRate)
              : undefined,
        },
      };
      const result = await onCreate(input);
      setCreated(result);
      setStep("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function applyToggleScope(scope: string) {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }

  function toggleScope(scope: string) {
    const turningOn = !scopes.includes(scope);
    if (turningOn && isAdminScope(scope)) {
      setPendingAdminScope(scope);
      return;
    }
    applyToggleScope(scope);
  }

  const canStep1 = selectedVendor !== null && name.trim().length > 0;
  const canStep2 = scopes.length > 0;
  const canStep3 =
    planType === "FreeTierCap"
      ? Number(includedCalls) > 0
      : planType === "FlatPerCall"
        ? Number(overageRate) >= 0 && overageRate.length > 0
        : Number(baseFee) >= 0 &&
          baseFee.length > 0 &&
          Number(includedCalls) >= 0 &&
          Number(overageRate) >= 0 &&
          overageRate.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          border: "1px solid rgb(226 232 240)",
          boxShadow: "0 4px 24px -8px rgb(15 23 42 / 0.12)",
          "--primary": whiteLabel.colorHex,
          "--primary-bg": `${whiteLabel.colorHex}10`,
        },
      }}
    >
      <DialogTitle className="flex items-center justify-between !pb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            {step === "done" ? "Oprettet" : `Trin ${step} af 3`}
          </div>
          <div className="text-lg font-semibold tracking-tight text-slate-900 mt-0.5">
            {step === "done" ? "Gem token nu" : "Opret API-nøgle"}
          </div>
        </div>
        <IconButton onClick={handleClose} size="small">
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className="!border-slate-200 !p-6 bg-slate-50/40">
        {error && (
          <div className="mb-4 px-3 py-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm">
            {error}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <Autocomplete<Organization, false, false, false>
              size="small"
              fullWidth
              options={vendorOptions}
              value={selectedVendor}
              loading={vendorLoading}
              onChange={(_, value) => setSelectedVendor(value)}
              onInputChange={(_, value) => setVendorQuery(value)}
              getOptionLabel={(option) =>
                option ? `${option.name} (#${option.id})` : ""
              }
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              filterOptions={(x) => x}
              noOptionsText={
                vendorQuery.trim().length < 2
                  ? "Skriv mindst 2 tegn"
                  : "Ingen forhandlere matcher"
              }
              renderOption={(props, option) => {
                const { key, ...rest } = props as { key?: React.Key } & Record<
                  string,
                  unknown
                >;
                return (
                  <Box
                    key={key as React.Key}
                    component="li"
                    {...(rest as React.HTMLAttributes<HTMLLIElement>)}
                  >
                    <div className="flex flex-col py-0.5">
                      <span className="text-xs text-slate-900">
                        {option.name}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        #{option.id}
                        {option.cvr ? ` · CVR ${option.cvr}` : ""}
                        {option.email ? ` · ${option.email}` : ""}
                      </span>
                    </div>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Forhandler"
                  placeholder="Søg på navn…"
                  helperText={
                    selectedVendor
                      ? `Forhandler #${selectedVendor.id}`
                      : "Søg på forhandlernavn. Mindst 2 tegn."
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {vendorLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Nøglens navn"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              helperText="Synligt navn, f.eks. 'Produktion'. Forhandleren ser det ikke."
            />
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="text-xs text-slate-500 mb-3">
              Vælg de endpoints, denne nøgle må kalde.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 max-h-[360px] overflow-y-auto pr-1">
              {knownScopes.map((s) => (
                <label
                  key={s.scope}
                  className="flex items-start gap-2 px-2 py-1.5 rounded-sm hover:bg-white border border-transparent hover:border-slate-200 cursor-pointer"
                >
                  <Checkbox
                    size="small"
                    checked={scopes.includes(s.scope)}
                    onChange={() => toggleScope(s.scope)}
                    sx={{ p: 0.25 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-slate-900">
                      {s.scope}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {s.routes.length}{" "}
                      {s.routes.length === 1 ? "rute" : "ruter"}
                    </div>
                  </div>
                </label>
              ))}
              {knownScopes.length === 0 && (
                <div className="col-span-2 text-xs text-slate-400 italic">
                  Ingen scopes fundet — backend kan være offline.
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">
                Prismodel
              </div>
              <RadioGroup
                value={planType}
                onChange={(e) => setPlanType(e.target.value as PlanType)}
              >
                {PLAN_TYPES.map((p) => (
                  <FormControlLabel
                    key={p.value}
                    value={p.value}
                    control={<Radio size="small" />}
                    label={
                      <span>
                        <span className="text-sm text-slate-900">
                          {p.label}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          {p.sub}
                        </span>
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormControl size="small" fullWidth>
                <InputLabel>Valuta</InputLabel>
                <Select
                  label="Valuta"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as Currency)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {planType === "MonthlyWithOverage" && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Faktureringsperiode</InputLabel>
                  <Select
                    label="Faktureringsperiode"
                    value={billingPeriod}
                    onChange={(e) =>
                      setBillingPeriod(e.target.value as BillingPeriod)
                    }
                  >
                    <MenuItem value="Weekly">Ugentligt</MenuItem>
                    <MenuItem value="Monthly">Månedligt</MenuItem>
                    <MenuItem value="Quarterly">Kvartalsvis</MenuItem>
                    <MenuItem value="Yearly">Årligt</MenuItem>
                  </Select>
                </FormControl>
              )}

              {(planType === "FreeTierCap" ||
                planType === "MonthlyWithOverage") && (
                <TextField
                  size="small"
                  label={
                    planType === "FreeTierCap"
                      ? "Samlet grænse"
                      : "Inkluderede kald per periode"
                  }
                  value={includedCalls}
                  onChange={(e) =>
                    setIncludedCalls(e.target.value.replace(/[^\d]/g, ""))
                  }
                />
              )}

              {planType === "MonthlyWithOverage" && (
                <TextField
                  size="small"
                  label={`Grundgebyr (${currency})`}
                  value={baseFee}
                  onChange={(e) =>
                    setBaseFee(e.target.value.replace(/[^\d.]/g, ""))
                  }
                />
              )}

              {(planType === "FlatPerCall" ||
                planType === "MonthlyWithOverage") && (
                <TextField
                  size="small"
                  label={`Pris per kald (${currency})`}
                  value={overageRate}
                  onChange={(e) =>
                    setOverageRate(e.target.value.replace(/[^\d.]/g, ""))
                  }
                  helperText={
                    planType === "MonthlyWithOverage"
                      ? "Faktureres for kald udover det inkluderede."
                      : undefined
                  }
                />
              )}
            </div>
          </div>
        )}

        {step === "done" && created && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              Token vises kun <span className="font-semibold">én gang</span>.
              Kopiér og udlevér til forhandleren — den kan ikke hentes igen senere.
            </div>
            <div className="bg-slate-900 rounded-sm border border-slate-700 px-4 py-3 flex items-center gap-3">
              <code className="font-mono text-xs text-emerald-300 break-all flex-1">
                {created.token}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(created.token);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="text-xs font-medium text-slate-300 hover:text-white px-2 py-1 border border-slate-700 hover:border-slate-500 rounded-sm transition-colors flex items-center gap-1.5"
              >
                <FontAwesomeIcon icon={faCopy} className="text-[10px]" />
                {copied ? "Kopieret" : "Kopiér"}
              </button>
            </div>

            <div className="border border-slate-200 rounded-sm bg-white">
              <div className="px-4 py-3 border-b border-slate-200 flex items-start gap-3">
                <span className="mt-0.5 h-7 w-7 shrink-0 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                  <FontAwesomeIcon icon={faShieldHalved} className="text-xs" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Del sikkert via NordPass
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Indsæt aldrig API-nøgler i e-mail, Teams eller Zendesk.
                    Brug i stedet et krypteret engangslink.
                  </div>
                </div>
              </div>
              <ol className="px-4 py-3 space-y-2 text-xs text-slate-700 list-decimal list-inside marker:text-slate-400 marker:font-semibold">
                <li>
                  Åbn{" "}
                  <a
                    href="https://nordpass.com/password-sharer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline decoration-slate-300 hover:decoration-slate-700 underline-offset-2"
                  >
                    NordPass-deleren
                  </a>{" "}
                  i en ny fane.
                </li>
                <li>
                  Indsæt token ovenfor i hemmelighedsfeltet. Ingen konto
                  nødvendig.
                </li>
                <li>
                  Sæt udløbstiden så kort som muligt (1–24 timer er rigeligt)
                  og begræns visninger til 1.
                </li>
                <li>Generér linket og send det til forhandleren.</li>
                <li>
                  Bed forhandleren gemme token straks — det krypterede link
                  selvdestruerer efter første visning eller ved udløb.
                </li>
              </ol>
              <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/40">
                <a
                  href="https://nordpass.com/password-sharer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-white bg-success hover:brightness-95 rounded-sm transition-all"
                >
                  Åbn NordPass-deleren
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="text-[10px]"
                  />
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <span className="text-slate-500">Nøgle-ID</span>
              <span className="font-mono text-slate-900">{created.id}</span>
              <span className="text-slate-500">Forhandler</span>
              <span className="text-slate-900">
                {selectedVendor?.name ?? "—"}{" "}
                <span className="font-mono text-slate-500">
                  #{created.organizationId}
                </span>
              </span>
              <span className="text-slate-500">Scopes</span>
              <span className="font-mono text-slate-900">
                {created.scopes.join(", ")}
              </span>
              <span className="text-slate-500">Udløber</span>
              <span className="font-mono text-slate-900">
                {created.expiresAt
                  ? new Date(created.expiresAt).toLocaleDateString("da-DK")
                  : "Aldrig"}
              </span>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions className="!px-6 !py-4 !border-t !border-slate-200">
        {step !== "done" && step !== 1 && (
          <button
            onClick={() => setStep(((step as number) - 1) as 1 | 2 | 3)}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
            disabled={busy}
          >
            Tilbage
          </button>
        )}
        <span className="flex-1" />
        {step === "done" ? (
          <button
            onClick={handleClose}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-success hover:brightness-95 rounded-sm transition-all"
          >
            Færdig
          </button>
        ) : step === 3 ? (
          <button
            onClick={handleSubmit}
            disabled={!canStep3 || busy}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-success hover:brightness-95 disabled:bg-slate-300 disabled:hover:brightness-100 rounded-sm transition-all"
          >
            {busy ? "Opretter…" : "Opret nøgle"}
          </button>
        ) : (
          <button
            onClick={() => setStep((step + 1) as 2 | 3)}
            disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-success hover:brightness-95 disabled:bg-slate-300 disabled:hover:brightness-100 rounded-sm transition-all"
          >
            Næste
          </button>
        )}
      </DialogActions>

      <ConfirmDialog
        open={pendingAdminScope !== null}
        title="Giv admin-adgang?"
        body={
          <>
            Scopet{" "}
            <code className="font-mono text-xs px-1 py-0.5 bg-slate-100 rounded-sm">
              {pendingAdminScope}
            </code>{" "}
            giver denne nøgle adgang til admin-endpoints — oprette og tilbagekalde
            API-nøgler, redigere prisplaner, ændre scopes på andre forhandlere.
            Giv kun dette til nøgler, der bruges af betroede interne medarbejdere
            eller selve admin-frontenden.
          </>
        }
        confirmLabel="Giv admin-scope"
        destructive
        onCancel={() => setPendingAdminScope(null)}
        onConfirm={() => {
          if (pendingAdminScope) applyToggleScope(pendingAdminScope);
          setPendingAdminScope(null);
        }}
      />
    </Dialog>
  );
}
